import React, { useState, useEffect } from 'react';
import {
	ChatContainer,
	ModalOverlay,
	ChatLayout,
} from './styles';
import { pipego } from 'api/pipego';
import { socketEvents } from 'lib/socket';
import { useWhatsappChatStore } from 'store/whatsappChat/useWhatsappChatStore';
import { ChatList } from './components/ChatList';
import { ChatArea } from './components/ChatArea';
import { Sidebar } from './components/Sidebar';
import { ModalStatusSession } from './components/ModalStatusSession';
import { useForm } from 'react-hook-form';
import { useMessage } from './context/MessageContext';
import useWebSocket from './context/useWebSocket.hook';
import { getWhatsappUserInfo } from 'util/isAdmin';
import { useNotifyAlert } from 'context/NotifyAlert';
import Settings from './components/Settings';
import { CloseButton } from './components/ChatArea/styles';
import { ACTIVE_CHATS_TAB, DASHBOARD_TAB, DEPARTMENTS_TAB, PENDING_CHATS_TAB, PHONE_NUMBERS_TAB, SETTINGS_TAB } from './utils/whatsappSIdebarTabs';
import { Dashboard } from './components/Dashboard';
import { Departments } from './components/Departments';
import { PhoneNumbersManager } from './components/PhoneNumbersManager';

const ChatWhatsappContent = () => {
	const { whatsappChat } = useWhatsappChatStore( ( state ) => state.states );
	const { closeWhatsappChat } = useWhatsappChatStore( ( state ) => state.actions );

	const form = useForm();
	const {
		checkWhatsAppStatus,
		setQrCode,
		handleChatClick,
		connectionStatus,
		qrCode,
		activeTab,
		setActiveTab,
		setConnectionStatus,
		selectedChat,
		conversations,
		loadChats,
		isLoadingChats,
		onChatUpdate,
		onMessageReceived,
		onMessageSend,
		onMessageUpdate,
		onChatsReload,
		setIsNewPhoneNumberModalOpen,
		handleFetchPhoneNumbers,
		onMessagingHistorySet,
		onSubSessionUpdate,
		hasMetaApi,
		setConnectionPhase,
		setPendingSubSessionId,
	} = useMessage();
	useWebSocket( onChatUpdate, onMessageReceived, onMessageSend, onChatsReload, onMessagingHistorySet, onSubSessionUpdate, onMessageUpdate );

	const { setShowNotify } = useNotifyAlert();

	const isOpen = whatsappChat.isOpen;
	const [ showUnreadOnly, setShowUnreadOnly ] = useState( false );
	const [ searchQuery, setSearchQuery ] = useState( '' );

	useEffect( () => {
		const checkStatusAndLoadChats = async () => {
			await checkWhatsAppStatus();
			loadChats();
		};
		checkStatusAndLoadChats();
		const offQRCode = socketEvents.onQRCode( ( qrData ) => {
			const newQrCode = qrData?.data?.data?.qr || null;
			setQrCode( newQrCode );
			if ( newQrCode ) {
				setConnectionPhase( 'waiting_scan' );
			}
		} );

		const offAuthenticated = socketEvents.onAuthenticated( ( status, subSessionId, isNewLogin ) => {
			if ( status === 'connected' ) {
				setConnectionStatus( prev => ( {
					...prev,
					isAuthenticated: true
				} ) );
				setQrCode( null );
				setConnectionPhase( 'idle' );
				setPendingSubSessionId( null );
				setIsNewPhoneNumberModalOpen( false );
				handleFetchPhoneNumbers();
			}

			// AFCO-417: 'isNewLogin' é o único sinal confiável de que o QR Code foi
			// lido — o Baileys o emite logo após configurar o pareamento. O status
			// 'pulling_wa_data' sozinho não serve: ele também é emitido quando um
			// socket novo abre, antes de existir QR, e fazia a tela piscar
			// "QR Code lido" logo depois de pedir um QR novo.
			if ( isNewLogin ) {
				setConnectionPhase( 'connecting' );
			}
		} );

		return () => {
			if ( typeof offQRCode === 'function' ) {
				offQRCode();
			}
			if ( typeof offAuthenticated === 'function' ) {
				offAuthenticated();
			}
		};
	}, [] ); //eslint-disable-line

	if ( !isOpen ) return null;

	const hasOficialChats = conversations.some( c => c.source === 'oficial' );

	if ( !connectionStatus.isAuthenticated && !hasOficialChats && !hasMetaApi && !isLoadingChats ) {
		return (
			<ModalStatusSession
				connectionStatus={connectionStatus}
				qrCode={qrCode}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
			/>
		);
	}

	return (
		<ModalOverlay>
			<ChatContainer>
				<CloseButton aria-label="Fechar" style={{ zIndex: 10, position: 'absolute', top: '16px', right: '16px', color: !selectedChat && (activeTab === ACTIVE_CHATS_TAB || activeTab === PENDING_CHATS_TAB) ? '#333' : 'white' }} onClick={closeWhatsappChat}>×</CloseButton>
				<ChatLayout>
					<Sidebar
						activeTab={activeTab}
						setActiveTab={setActiveTab}
					/>
					{activeTab === SETTINGS_TAB && (
						<>
							<Settings
								onBack={() => setActiveTab( 'chats' )}
								onLogout={async () => {
									try {
										const isAdminProfileType = getWhatsappUserInfo()?.profile_type_id === 1;
										if ( isAdminProfileType ) {
											await pipego.logoutWhatsApp();
											checkWhatsAppStatus();
										} else {
											setShowNotify( {
												status : 'error',
												text   : 'Usuário não autorizado'
											} );
										}
									} catch ( error ) {
										console.error( 'Error logging out:', error );
									}
								}}
								qrCode={qrCode}
								isAuthenticated={connectionStatus.isAuthenticated}
								isLoading={!connectionStatus.hasQR && !connectionStatus.isAuthenticated}
							/>
						</>
					)}
					{activeTab === ACTIVE_CHATS_TAB && (
						<div style={{
							display  : 'flex',
							width    : '100%',
							height   : '100%',
							position : 'relative',
							flex     : '1 1 auto'
						}}>
							<div style={{
								width      : '35%',
								height     : '100%',
								position   : 'relative',
								flexShrink : 0
							}}>
								<ChatList
									conversations={conversations.filter( conversation => conversation?.userId ).sort( ( a, b ) => {
										const dateA = new Date( a?.createdAt || 0 );
										const dateB = new Date( b?.createdAt || 0 );
										return dateB - dateA;
									} )}
									searchQuery={searchQuery}
									showUnreadOnly={showUnreadOnly}
									setShowUnreadOnly={setShowUnreadOnly}
									setSearchQuery={setSearchQuery}
									handleChatClick={handleChatClick}
									selectedConversation={selectedChat}
									activeTab={activeTab}
								/>
							</div>
							<div style={{
								width      : '65%',
								height     : '100%',
								position   : 'relative',
								flexShrink : 0
							}}>
								{selectedChat ? (
									<ChatArea
										useForm={form}
									/>
								) : (
									<div style={{
										display         : 'flex',
										alignItems      : 'center',
										justifyContent  : 'center',
										height          : '100%',
										backgroundColor : '#f0f2f5',
										color           : '#667781'
									}}>
										Selecione um chat para começar
									</div>
								)}
							</div>
						</div>
					)}
					{activeTab === PENDING_CHATS_TAB && (
						<div style={{
							display  : 'flex',
							width    : '100%',
							height   : '100%',
							position : 'relative',
							flex     : '1 1 auto'
						}}>
							<div style={{
								width      : '35%',
								height     : '100%',
								position   : 'relative',
								flexShrink : 0
							}}>
								<ChatList
									conversations={conversations
										.filter( conversation => !conversation?.userId )
										.sort( ( a, b ) => {
											const dateA = new Date( a?.createdAt || 0 );
											const dateB = new Date( b?.createdAt || 0 );
											return dateB - dateA;
										} )}
									searchQuery={searchQuery}
									showUnreadOnly={showUnreadOnly}
									setShowUnreadOnly={setShowUnreadOnly}
									setSearchQuery={setSearchQuery}
									handleChatClick={handleChatClick}
									selectedConversation={selectedChat}
									activeTab={activeTab}
								/>
							</div>
							<div style={{
								width      : '65%',
								height     : '100%',
								position   : 'relative',
								flexShrink : 0
							}}>
								{selectedChat ? (
									<ChatArea
										useForm={form}
									/>
								) : (
									<div style={{
										display         : 'flex',
										alignItems      : 'center',
										justifyContent  : 'center',
										height          : '100%',
										backgroundColor : '#f0f2f5',
										color           : '#667781'
									}}>
										Selecione um chat para começar
									</div>
								)}
							</div>
						</div>
					)}
					{activeTab === DASHBOARD_TAB && (
						<Dashboard />
					)}
					{activeTab === DEPARTMENTS_TAB && (
						<Departments />
					)}
					{activeTab === PHONE_NUMBERS_TAB && (
						<PhoneNumbersManager />
					)}
				</ChatLayout>
			</ChatContainer>
		</ModalOverlay>
	);
};

const ChatWhatsapp = () => {
	return (
		<ChatWhatsappContent />
	);
};

export { ChatWhatsapp };
